import { showToast } from './Toast';
import React, { useState } from 'react';
import { X, ShoppingCart, Minus, Plus, Trash2, CheckCircle } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export const ShoppingCartSidebar = ({ isOpen, onClose }) => {
  const { cartItems, updateCartItem, removeFromCart, calculateTotal, isLoading } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    await updateCartItem(itemId, newQuantity);
  };

  const removeItem = async (itemId) => {
    const confirmed = window.confirm('آیا از حذف این محصول اطمینان دارید؟');
    if (confirmed) {
      await removeFromCart(itemId);
    }
  };

  const applyCoupon = () => {
    if (couponCode.toUpperCase() === 'GOLD20') {
      setDiscount(20);
    } else if (couponCode.toUpperCase() === 'SPRING10') {
      setDiscount(10);
    } else {
      showToast('کد تخفیف معتبر نیست', 'error');
      setDiscount(0);
    }
  };

  const calculateSubtotal = () => {
    return calculateTotal();
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white p-6 border-b z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">سبد خرید</h2>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {cartItems.length} محصول در سبد خرید شما
          </p>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">در حال بارگذاری...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">سبد خرید شما خالی است</p>
              <button 
                onClick={onClose}
                className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow"
              >
                ادامه خرید
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex gap-4">
                      <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg flex-shrink-0">
                        {item.product?.image && (
                          <img 
                            src={item.product.image} 
                            alt={item.product?.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{item.product?.name || 'محصول'}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.product?.weight} گرم | {item.product?.karat} عیار
                        </p>
                        {/* ✅ نمایش وضعیت موجودی */}
                        {item.product?.stock != null && item.product.stock !== Infinity && (
                          <p className={`text-xs mt-1 ${
                            item.quantity >= item.product.stock
                              ? 'text-red-500 font-semibold'
                              : item.product.stock - item.quantity <= 2
                              ? 'text-amber-600'
                              : 'text-gray-400'
                          }`}>
                            {item.quantity >= item.product.stock
                              ? `⚠️ به سقف موجودی (${item.product.stock} عدد) رسیدید`
                              : `${item.product.stock - item.quantity} عدد دیگر موجود`}
                          </p>
                        )}

                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={isLoading}
                              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-semibold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={
                                isLoading ||
                                (item.product?.stock != null &&
                                  item.product.stock !== Infinity &&
                                  item.quantity >= item.product.stock)
                              }
                              title={
                                item.product?.stock != null &&
                                item.product.stock !== Infinity &&
                                item.quantity >= item.product.stock
                                  ? `سقف موجودی: ${item.product.stock} عدد`
                                  : ''
                              }
                              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="text-right">
                            <p className="font-bold text-amber-600">
                              {(item.price_at_time * item.quantity).toLocaleString()} تومان
                            </p>
                            {item.quantity > 1 && (
                              <p className="text-xs text-gray-500">
                                {item.price_at_time.toLocaleString()} × {item.quantity}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={isLoading}
                        className="text-red-500 hover:text-red-600 transition-colors flex-shrink-0 disabled:opacity-50"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 mb-4">
                <h3 className="font-semibold mb-3">کد تخفیف</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="کد تخفیف را وارد کنید"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button
                    onClick={applyCoupon}
                    className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    اعمال
                  </button>
                </div>
                {discount > 0 && (
                  <div className="mt-2 p-2 bg-green-50 rounded-lg">
                    <p className="text-green-600 text-sm flex items-center">
                      <CheckCircle className="w-4 h-4 ml-1" />
                      {discount}% تخفیف اعمال شد
                    </p>
                  </div>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  کدهای تخفیف: GOLD20 (20%) یا SPRING10 (10%)
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">خلاصه سفارش</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">جمع کل</span>
                    <span className="font-semibold">
                      {calculateSubtotal().toLocaleString()} تومان
                    </span>
                  </div>
                  
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>تخفیف ({discount}%)</span>
                      <span>
                        -{((calculateSubtotal() * discount) / 100).toLocaleString()} تومان
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>هزینه ارسال</span>
                    <span className="text-green-600">رایگان</span>
                  </div>
                  
                  <div className="flex justify-between font-bold text-lg pt-3 border-t mt-3">
                    <span>مبلغ قابل پرداخت</span>
                    <span className="text-amber-600">
                      {calculateFinalTotal().toLocaleString()} تومان
                    </span>
                  </div>
                </div>

                <button 
                  className="w-full mt-6 bg-gradient-to-r from-amber-400 to-yellow-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow"
                  onClick={() => window.location.href = '/checkout'}
                >
                  تکمیل خرید
                </button>
                
                <button 
                  onClick={onClose}
                  className="w-full mt-3 border border-gray-300 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  ادامه خرید
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShoppingCartSidebar;






